import { cn } from "@lib/utils";

/**
 * Compound component for deploy screen layouts.
 *
 * Zones:
 *  - Header: flex row with title on left, actions on right
 *  - Body:   flex-1 scrollable content area
 *  - Footer: bottom-pinned content
 */

function Root({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col min-h-full", className)}>
      {children}
    </div>
  );
}

function Header({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-start justify-between gap-4 shrink-0 px-10 pt-7", className)}>
      {children}
    </div>
  );
}

function Title({ children, description, className }: { children: React.ReactNode; description?: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex flex-col gap-0.5 min-w-0", className)}>
      <h2 className="text-lg font-semibold leading-[22px] text-base-500">{children}</h2>
      {description && (
        <p className="text-xs leading-4 text-base-300">{description}</p>
      )}
    </div>
  );
}

function Actions({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("flex items-center gap-2 shrink-0", className)}>
      {children}
    </div>
  );
}

function Body({ children, className, centered }: { children: React.ReactNode; className?: string; centered?: boolean }) {
  return (
    <div className={cn(
      "flex-1 min-h-0 flex flex-col",
      centered && "items-center justify-center",
      className,
    )}>
      {children}
    </div>
  );
}

function Footer({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={cn("shrink-0", className)}>
      {children}
    </div>
  );
}

export const DeployPanel = { Root, Header, Title, Actions, Body, Footer };
